/**
 * Award-data SSOT — Health Professionals and Support Services Award MA000027
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ISOLATION GUARANTEE
 * ═══════════════════════════════════════════════════════════════════════
 *
 * This file encodes **MA000027-specific constants ONLY**. DO NOT add fields
 * from other awards' provisions even if they share field names. Cross-award
 * shape-porting risk per `project_cross_award_shape_porting_risk.md` is
 * mitigated by this isolation. See runbook §7.
 *
 * HPSS recall is UNIFIED at cl 25.6 (2h flat, no in-person/electronic split,
 * no travel-counts, no within-3h carveout). Mirrors Aged Care cl 25.1(e)
 * and SCHADS cl 28.4. Does NOT mirror Nurses MA000034 split shape.
 *
 * HPSS has NO FIRST_AID allowance (cf. Aged Care cl 15.6 / Nurses cl 22).
 * The pre-Phase-1I.4 engine emitted a FIRST_AID line which was the 1st
 * confirmed cross-award shape-porting defect.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * SOURCES + INDEXATION
 * ═══════════════════════════════════════════════════════════════════════
 *
 * Primary sources for every value below:
 *   - FWC consolidated MA000027
 *   - FWO Pay Guide G01002986 (or current equivalent)
 *   - FWC determination PR791120 (HPSS Stage 3, ppc 1 Oct 2025)
 *   - FWC determination PR795400 (HPSS classification scale clarification)
 *
 * HPSS rate data lives in DB (`award_rate_lines`) and is consumed by the
 * engine via `RateContext`. This file carries the engine's structural
 * RULES (recall, shift classification, classification streams) that are
 * NOT in the DB rate-lines table.
 *
 * AWR 2026 hard deadline for reseed: 22 Jun 2026.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * KNOWN GAPS (post-Madea; HPSS production-readiness gate W3)
 * ═══════════════════════════════════════════════════════════════════════
 *
 *   - cl 11.2 casual minimum payment-floor (3h) enforcement not engine-consumed (W3).
 *   - cl 11.3 casual minimum payment-floor (2h) for cleaners in private medical practice (W3).
 *   - cl 23.3(e)(ii) MEAL_OT_ADDITIONAL second-meal emission path — CLOSED Phase 1O (2026-05-17); see HPSS_MEAL_OT_EMISSION below.
 *   - cl 16.7 adult-apprentice (≥21) floor overlay (W7; 0 adult apprentices in DB).
 *   - B.1 shift-loading single-band collapse (W8; numerically benign today).
 */

import type { OtRateBands, RecallRules, ShiftCharacterRules } from './_shared';

// ═══════════════════════════════════════════════════════════════════════
// Shift classification rules (Schedule C.1)
// ═══════════════════════════════════════════════════════════════════════

/**
 * HPSS Award MA000027 cl 26.3 OR-triggered single-band shift loading.
 *
 * Phase 1A.5 (2026-05-16) — first cut, finish-only Nurses-shape port.
 * Phase Codex Pass A (2026-05-19) — REBUILT per cl 26.3 primary-source
 *   verification. The pre-Phase finish-only `{ afternoon: ..., night: ... }`
 *   shape mis-classified 18:00-08:30 commence-in-window-but-finish-out-of-
 *   window shifts as DAY (no loading), under-paying 15% × ordinary × hours.
 *   FW Act s.327A wage-theft criminal regime exposure.
 *
 * Verbatim cl 26.3 (FWC consolidated MA000027, Tanda template confirms):
 *
 *   "Where the ordinary rostered hours of work of a shiftworker finish
 *    between 6.00 pm and 8.00 am OR commence between 6.00 pm and 6.00 am,
 *    the employee will be paid 115% of the minimum hourly rate of pay
 *    applicable to their classification and pay point."
 *
 * Single 115% permanent / 140% casual band. NO afternoon/night split.
 * OR-trigger: a shift triggers the band if EITHER predicate holds.
 *
 *   - Finish predicate: finishes between 18:00 and 08:00 (inclusive of 08:00).
 *   - Commence predicate: commences between 18:00 (inclusive) and 06:00 (exclusive).
 *
 * The classifier returns `shiftCharacter: 'AFTERNOON'` for any shift
 * matching either predicate. The HPSS engine routes 'AFTERNOON' to the
 * `eveningShift` band, paid at the single 115% loading (matches the
 * pre-Phase DB seed `WEEKDAY_EVENING` = 1.15 / `WEEKDAY_NIGHT` = 1.15;
 * either band code is dollar-equivalent because both are 115%). The
 * shape-historic 'AFTERNOON' name is dollar-correct; the cl 26.3 spec
 * has no afternoon/night distinction.
 *
 * Counter-examples now correctly classified as AFTERNOON (115% loading):
 *   - 18:00-08:30 — commences 18:00 → commence-window OR-trigger fires
 *   - 17:00-04:00 — commences 17:00 (out) BUT finishes 04:00 (in window) → finish OR-trigger
 *   - 20:00-04:00 — commences 20:00 AND finishes 04:00 → both predicates
 *
 * Source: FWC consolidated MA000027 cl 26.3 + Tanda MA000027 template.
 * Verified: Phase Codex Pass A (2026-05-19) statura-regulatory-source-agent.
 * Artifact: docs/operations/clause-verifications/2026-05-19-statura-regulatory-source-shift-classification-primary-source-rebuild.md
 */
export const HPSS_SHIFT_RULES: ShiftCharacterRules = {
  orTrigger: {
    commenceAtOrAfterHour: 18, // cl 26.3 "commence between 6.00 pm..."
    commenceBeforeHour: 6, // cl 26.3 "...and 6.00 am" (wraps midnight)
    finishAfterHour: 18, // cl 26.3 "finish between 6.00 pm..."
    finishAtOrBeforeHour: 8, // cl 26.3 "...and 8.00 am"
  },
};

// ═══════════════════════════════════════════════════════════════════════
// Recall-to-duty rules (cl 25.6) — Phase 1I.1 primary-source rewrite
// ═══════════════════════════════════════════════════════════════════════

/**
 * Phase 1I.1 (Award Engine Integrity 2026-05-16) — HPSS recall-to-duty
 * rules per MA000027 cl 25.6 (verified against current published award
 * text + Fair Work Pay Guide G01002984).
 *
 * Corrects Phase 1C commit b2e8dcda, which shipped HPSS_RECALL_RULES as a
 * copy of the Nurses MA000034 cl 19.6 + 19.7 shape with the explicit
 * qualifier `'HPSS Award MA000027 cl 22 (recall to duty — subclause to
 * verify pre-prod)'`. The Phase 1I.0 four-cluster sweep established that
 * HPSS cl 25.6 is fundamentally different from the Nurses shape:
 *
 *   - The clause is **MA000027 cl 25.6**, NOT cl 22.
 *   - 2-hour flat minimum at OT for any recall after leaving the
 *     premises. No in-person / electronic distinction — both pay the
 *     same 2h floor.
 *   - Travel time does NOT count as time worked under HPSS.
 *   - No within-rostered-start carveout.
 *   - Casual loading: HPSS cl 11.4 + cl 26.1(b) hybrid model — OT
 *     multiplier embeds the 25% casual loading.
 *
 * Wage-theft exposure under the defective Phase 1C rules:
 *   - 30-min electronic recall: paid 1h, should be 2h → underpayment.
 *
 * Source: FWC consolidated MA000027 cl 25.6 + Pay Guide G01002984.
 * Verified: 2026-05-16 (Phase 1I.1).
 * Artifact: docs/operations/clause-verifications/2026-05-16-aged-care-legislation-hpss-tier-2-3-sweep.md
 * Errata: #9 (verify-pre-prod qualifier resolution).
 */
/**
 * Phase Codex Pass A PR #B (2026-05-19) — HPSS MA000027 OT rate
 * bands for banded recall pay per cl 25.2 + cl 25.3 + cl 25.6.
 *
 * Verbatim cl 25.2 / cl 25.3 (FWC consolidated MA000027, specialist
 * primary-source verification 2026-05-19):
 *
 *   "25.2 Overtime rates for full-time and part-time employees:
 *    (a) For all time worked in excess of the ordinary hours or outside
 *        the daily span of hours, overtime rates apply.
 *    (b)(i) Monday to Saturday—first 2 hours: 150%; after 2 hours: 200%;
 *       (ii) Sunday: 200%;
 *       (iii) Public holiday: 250%."
 *
 *   "25.3 Overtime rates for casual employees:
 *    NOTE: For a casual employee, the rates in this clause are
 *    calculated by adding the casual loading prescribed in clause 10.4
 *    to the percentages in clause 25.2 before applying the overtime rates.
 *    (b)(i) Monday to Saturday—first 2 hours: 187.5% (= 150% × 1.25);
 *           after 2 hours: 250% (= 200% × 1.25);
 *       (ii) Sunday: 250% (= 200% × 1.25);
 *       (iii) Public holiday: 312.5% (= 250% × 1.25)."
 *
 * **CRITICAL FINDING (specialist §3, 2026-05-19):** the original Codex
 * brief cited cl 28.2 — that is WRONG. cl 28 is "Personal/carer's
 * leave"; the OT clauses are cl 25.2 (FT/PT) and cl 25.3 (casual).
 * cl 25.6 is the RECALL trigger clause (2h floor).
 *
 * **CASUAL LOADING NOTE — MULTIPLICATIVE INTERPRETATION:** Per the
 * cl 25.3 NOTE wording "adding the casual loading ... before applying
 * the overtime rates", the casual loading is added to the BASE before
 * the OT multiplier is applied, which is mathematically equivalent to
 * multiplying the OT rate by 1.25. This matches Fair Work's published
 * Pay Guide values (187.5 / 250 / 250 / 312.5) and matches AC cl 25.1
 * NOTE + Nurses cl 19.2(a) "% of casual hourly rate" + SCHADS cl 28.1
 * settled doctrine. All four awards use the unified MULTIPLICATIVE
 * treatment post-Phase-Codex-A-B.
 *
 * Source: FWC consolidated MA000027 cl 25.2 + cl 25.3 + cl 25.6 +
 *         Pay Guide G01002984.
 * Verified: 2026-05-19 (Phase Codex Pass A PR #B specialist invocation;
 *           statura-regulatory-source-agent autonomous mode).
 * Artifact: docs/operations/clause-verifications/2026-05-19-statura-regulatory-source-recall-banded-ot.md
 */
export const HPSS_OT_RATE_BANDS: OtRateBands = {
  weekdayTier1Hours: 2, // cl 25.2(b)(i) / cl 25.3(b)(i) "first 2 hours"
  weekdayTier1: {
    permanent: 1.5, // cl 25.2(b)(i) Mon-Sat first 2h — 150%
    casual: 1.875, // cl 25.3 NOTE (multiplicative interpretation per specialist artifact)
  },
  weekdayTier2: {
    permanent: 2.0, // cl 25.2(b)(i) Mon-Sat after 2h — 200%
    casual: 2.5, // 2.0 × 1.25
  },
  // Saturday NOT flat — joins Mon-Fri tier curve per cl 25.2(b)(i) / cl 25.3(b)(i).
  // saturdayFlat is intentionally omitted; do NOT port AC cl 25.1(a)(i)(B).
  sunday: {
    permanent: 2.0, // cl 25.2(b)(ii) / cl 25.3(b)(ii) Sunday — 200%
    casual: 2.5, // 2.0 × 1.25
  },
  publicHoliday: {
    permanent: 2.5, // cl 25.2(b)(iii) / cl 25.3(b)(iii) PH — 250%
    casual: 3.125, // 2.5 × 1.25
  },
  clauseRefs: {
    weekdayTier1:
      'HPSS Award MA000027 cl 25.6 recall trigger + cl 25.2(b)(i) FT/PT or cl 25.3(b)(i) casual (Mon-Sat OT first 2 hours — 150% / 187.5%)',
    weekdayTier2:
      'HPSS Award MA000027 cl 25.6 recall trigger + cl 25.2(b)(i) FT/PT or cl 25.3(b)(i) casual (Mon-Sat OT after 2 hours — 200% / 250%)',
    sunday:
      'HPSS Award MA000027 cl 25.6 recall trigger + cl 25.2(b)(ii) FT/PT or cl 25.3(b)(ii) casual (Sunday OT flat — 200% / 250%)',
    publicHoliday:
      'HPSS Award MA000027 cl 25.6 recall trigger + cl 25.2(b)(iii) FT/PT or cl 25.3(b)(iii) casual (PH OT flat — 250% / 312.5%)',
  },
};

export const HPSS_RECALL_RULES: RecallRules = {
  awardCode: 'MA000027',
  inPersonMinimumHours: 2,
  electronicMinimumHours: 2,
  travelCountsAsTimeWorked: false,
  electronicRoundingMinutes: 15,
  withinRosteredStartCarveoutHours: 0,
  // Phase Codex Pass A PR #B (2026-05-19) — corrected from 'INCLUSIVE' to
  // 'MULTIPLICATIVE' per HPSS cl 25.3 NOTE primary-source verification
  // (specialist artifact §3 + §5). 'INCLUSIVE' pre-fix silently underpaid
  // casuals by 25% on the OT multiplier because ctx penalty bands carry
  // ordinary-hour casual rates, not OT casual rates.
  casualLoadingTreatment: 'MULTIPLICATIVE',
  otBands: HPSS_OT_RATE_BANDS,
  clauseRef:
    'HPSS Award MA000027 cl 25.6 (recall to duty) read with cl 25.2 FT/PT + cl 25.3 casual banded OT rates',
};

// ═══════════════════════════════════════════════════════════════════════
// Classification streams
// ═══════════════════════════════════════════════════════════════════════

/**
 * Classification streams used by the HPSS engine.
 *
 * Phase 1I.4 (Award Engine Integrity 2026-05-16) — added `cooking_apprentice`
 * stream per cl 16.4. cl 16.8(a) explicitly applies all award conditions to
 * apprentices, so the same penalty / OT / shift-loading matrix used for
 * adult Support Services workers applies with the apprentice base rate
 * (55% / 65% / 80% / 95% of cl 16.2(a) Level 4 by year of indenture).
 * Rate rows are seeded under classification_stream='cooking_apprentice',
 * classification_level 'Y1'/'Y2'/'Y3'/'Y4'.
 *
 * Source: FWC consolidated MA000027 cl 16.2 + cl 16.4 + cl 16.8(a).
 * Verified: 2026-05-16 (Phase 1I.4).
 */
export const HPSS_STREAMS: ReadonlySet<string> = new Set([
  'health_professional',
  'support_services',
  'cooking_apprentice',
]);

// ═══════════════════════════════════════════════════════════════════════
// Meal-OT emission semantics (cl 23.3(e)) — Phase 1O
// ═══════════════════════════════════════════════════════════════════════

/**
 * Phase 1O (Award Engine Integrity 2026-05-17) — HPSS Award MA000027
 * meal-OT allowance emission semantics per cl 23.3(e).
 *
 * Closes the W3 KNOWN GAP listed at the head of this file
 * ("cl 23.3(e)(ii) MEAL_OT_ADDITIONAL second-meal emission path (W3)").
 *
 * Verbatim cl 23.3(e) (FWC consolidated MA000027, primary-source verified
 * W22 specialist invocation 2026-05-17 — fwc.gov.au/documents/modern_awards/pdf/ma000027.pdf,
 * amendments to 1 Apr 2026, latest PR795400 / PR797960; variation
 * history PR786730 ppc 01Jul25):
 *
 *     "(e) Meal allowances
 *      (i) When required to work after the usual finishing hour of work
 *          beyond one hour or, in the case of shiftworkers, when the
 *          overtime work on any shift exceeds one hour, an employee will
 *          be:
 *          • supplied with an adequate meal where an employer has
 *            adequate cooking and dining facilities; or
 *          • paid a meal allowance of $16.62.
 *      (ii) In addition to the allowance provided for in clause 23.3(e)(i),
 *           where overtime work exceeds 4 hours, a further meal allowance
 *           of $14.98 will be paid.
 *      (iii) Clauses 23.3(e)(i) and 23.3(e)(ii) will not apply when an
 *            employee could reasonably return home for a meal within the
 *            meal break."
 *
 * Pre-Phase-1O the HPSS engine emitted ONE MEAL_ALLOWANCE under
 * cl 23.3(e), triggered by the caller `hasDelayedMealAllowance` boolean —
 * silently dropping the cl 23.3(e)(ii) further-meal allowance ($14.98
 * per OT > 4h event). Direction: under-pay. FW Act s.327A wage-theft
 * criminal regime exposure CONDITIONAL on any HPSS OT > 4h shift
 * hitting the engine before Phase 1O. W22 evidence pack at
 * `docs/operations/clause-verifications/2026-05-17-aged-care-legislation-nurses-cl-17-3-b-meal-ot.md`.
 *
 * **Cross-award native symmetry (binding per `project_cross_award_shape_porting_risk.md`)**:
 * MA000027 cl 23.3(e) is STRUCTURALLY IDENTICAL to MA000018 cl 15.4 and
 * MA000034 cl 17.3(b) (same template, same triggers, same dollar amounts —
 * variation determination cycle PR786730/PR786721/PR786738 all ppc 01Jul25).
 * Despite this symmetry, encode the MA000027 constants INDEPENDENTLY here —
 * DO NOT factor `MealOtRules` into `_shared.ts`. The shape-porting binding
 * rule fires in BOTH directions (5th/6th-instance pattern surfaced W22):
 * over-applying one award's shape OR under-applying another's both fail
 * if the structs are factored together and the FWC later diverges them.
 *
 * **W4 docstring errata (#34)**: the AGED_CARE_MEAL_OT_EMISSION docstring
 * at `award-data/aged-care.ts:298-307` incorrectly cited HPSS cl 18.7(a)
 * at $13.29 / $11.98 as the HPSS meal-OT provision. Phase 1O primary-
 * source verification (W22) confirms the correct provision is cl 23.3(e)
 * at $16.62 / $14.98 (matching the DB rate-registry seed at
 * `supabase/migrations/20260610100000_seed_hpss_v1.sql:199`). The W4
 * docstring is corrected in this commit.
 *
 * Source: FWC consolidated MA000027 cl 23.3(e). Pay Guide G01002984
 * (FWO 1 Oct 2025 republication). Amounts pinned via DB rate-registry
 * seed `supabase/migrations/20260610100000_seed_hpss_v1.sql` (`MEAL`
 * PER_INCIDENT $16.62 cl 23.3(e)(i); `MEAL_OT_ADDITIONAL` PER_INCIDENT
 * $14.98 cl 23.3(e)(ii)) — both seeded pre-Phase-1O, engine reads via
 * `findAllowance(ctx.allowances, ..., employment)`.
 * Verified: 2026-05-17 (W22 aged-care-legislation specialist).
 * Artifact: docs/operations/clause-verifications/2026-05-17-aged-care-legislation-nurses-cl-17-3-b-meal-ot.md
 */
export const HPSS_MEAL_OT_EMISSION = {
  awardCode: 'MA000027' as const,
  /** DB lookup key for first meal $16.62 (PER_INCIDENT basis). */
  firstMealAllowanceCode: 'MEAL' as const,
  /** OT must exceed this many hours past usual finish for first meal to fire. */
  firstMealThresholdHours: 1.0 as const,
  /** DB lookup key for additional meal $14.98 (PER_INCIDENT basis). */
  secondMealAllowanceCode: 'MEAL_OT_ADDITIONAL' as const,
  /** Cumulative OT must exceed this many hours for second meal to fire (in addition to first). */
  secondMealThresholdHours: 4.0 as const,
  /** CUMULATIVE semantics: second meal is ADDED to first, not exclusive. */
  cumulative: true as const,
  /** NOT all-purpose — flat emission; casual loading does NOT compound. */
  appliesToCasualLoading: false as const,
  clauseRefFirst:
    'HPSS Award MA000027 cl 23.3(e)(i) (meal allowance — OT > 1h past usual finish; supplied-meal carve-out applies)',
  clauseRefSecond:
    'HPSS Award MA000027 cl 23.3(e)(ii) (further meal allowance — OT > 4h cumulative; additive to first meal)',
};

// ═══════════════════════════════════════════════════════════════════════
// Leave entitlements (NES + MA000027)
// ═══════════════════════════════════════════════════════════════════════

/**
 * Annual leave loading under HPSS Award MA000027 clause 27.3.
 *
 * The FWC consolidated award, current to 1 July 2026, requires 17.5% of the
 * minimum rate for non-shiftworkers and the higher-of comparison for
 * shiftworkers. This constant supplies the flat component; the pay-run
 * snapshot owns the roster-evidence comparison.
 */
export const HPSS_LEAVE = {
  annualLeaveLoading: 0.175,
  clauseRef:
    'HPSS Award MA000027 cl 27.3(a) (annual leave loading — 17.5% of the minimum rate; cl 27.3(b) higher-of applied by pay-run leave snapshot from roster evidence)',
} as const;
